import { normalizeApiError } from '../api/apiError';

const MESSAGES: Record<string, string> = {
  ATTENDANCE_NOT_FOUND: 'Attendance record was not found.',
  ACCESS_DENIED: 'You do not have permission to view this attendance record.',
  VALIDATION_ERROR: 'Check the selected attendance filters.',
  WFH_DEVICE_REQUIRED:
    'A device identifier is required for work-from-home check-in.',
  WFH_CHECK_IN_PENDING: 'WFH request is pending approval.',
  WFH_CHECK_IN_REJECTED: 'WFH request was rejected.',
  WFH_CHECK_IN_NOT_APPROVED:
    'WFH check-in requires an approved WFH request for this date.',
  UNSUPPORTED_ATTENDANCE_SOURCE:
    'This work location cannot be recorded yet. Ask an administrator to allow Client Office and Client/Prospect Visit attendance.',
  INVALID_DATE_RANGE: 'The selected date range is invalid.',
  NETWORK_ERROR: 'Unable to connect to the server. Check your connection and try again.',
  REQUEST_TIMEOUT: 'The server took too long to respond. Your attendance may already be saved — refresh and check your status before trying again.',
  EMPLOYEE_PROFILE_REQUIRED: 'An employee profile is required for attendance.',
  ATTENDANCE_ALREADY_CHECKED_IN: 'You are already checked in for today.',
  ATTENDANCE_NOT_CHECKED_IN: 'Check in before checking out or starting a break.',
  ATTENDANCE_ALREADY_COMPLETE: 'Attendance for today is already complete.',
  BREAK_ALREADY_STARTED: 'A break is already in progress.',
  BREAK_NOT_STARTED: 'No open break was found to end.',
  FACE_VERIFICATION_EXPIRED:
    'Face verification expired. Please capture your face again and retry.',
  FACE_VERIFICATION_ALREADY_USED:
    'That face verification was already used. Capture your face again if your attendance was not recorded.',
  FACE_VERIFICATION_REQUIRED: 'Face verification is required before marking attendance.',
  FACE_IMAGE_REQUIRED: 'A face photo is required before marking attendance.',
  RATE_LIMIT_EXCEEDED: 'Too many attendance attempts. Wait a moment and try again.',
  BIOMETRIC_TIMEOUT: 'Face recognition timed out. Please try again.',
  BIOMETRIC_RATE_LIMITED: 'Face recognition is busy. Wait a moment and try again.',
  BIOMETRIC_PROVIDER_UNAVAILABLE:
    'Face recognition is temporarily unavailable. Please try again shortly.',
  SESSION_EXPIRED: 'Your session expired. Sign in again and retry.',
  INVALID_SESSION: 'Your session expired. Sign in again and retry.',
};

export function attendanceErrorMessage(
  error: unknown,
  fallback = 'Unable to load attendance history.',
): string {
  if (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    typeof (error as { code?: unknown }).code === 'string'
  ) {
    const code = (error as { code: string }).code;
    if (MESSAGES[code]) return MESSAGES[code];
  }

  const apiError = normalizeApiError(error);
  if (apiError.code && MESSAGES[apiError.code]) {
    return MESSAGES[apiError.code];
  }
  if (apiError.message?.trim()) {
    return apiError.message.trim();
  }
  if (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as { message?: unknown }).message === 'string' &&
    (error as { message: string }).message.trim()
  ) {
    const message = (error as { message: string }).message.trim();
    if (
      message.includes('date range') ||
      message.includes('fromDate') ||
      message.includes('toDate')
    ) {
      return MESSAGES.INVALID_DATE_RANGE;
    }
  }
  return fallback;
}
